/**
 * dev-tenant-id — runtime DEV tenant-id resolver (F7b T7b.9).
 *
 * W DEV backend middleware usunięty w F15a (ADR-0009, 2026-05-11) — host resolution per-route.
 * Vite proxy `/api` ma `changeOrigin: true` → Host leci jako `localhost:3001` (backend
 * rozwiązuje to jako `dev` scope, nie tenant) → endpointy tenantowe czytają `x-tenant-id`
 * z request headers. Frontend axios interceptor (`lib/api.ts`) wstrzykuje ten header dla
 * tenant scope. Źródło tenant-id ma 3 warstwy (first-hit wins):
 *
 *  1. **Query param `?__devTenantId=<uuid>`** (runtime override) — przechwycony z URL przy
 *     pierwszym wywołaniu, stash w sessionStorage, usunięty z URL (replaceState). Używany
 *     przez Register redirect (cross-subdomain handoff w DEV) i Playwright E2E.
 *  2. **sessionStorage** — persist po step 1, żyje w obrębie taba. Pozwala przeżyć SPA reload
 *     po cross-subdomain redirect bez zachowywania query w URL.
 *  3. **`VITE_DEV_TENANT_ID`** (build-time fallback) — istniejący workflow T7b.8: dev edituje
 *     `.env.local`, restart Vite, single-tenant preview. Zachowany dla backward compat.
 *
 * W PROD funkcja zwraca null (subdomeny + real host resolver + cookies załatwiają scope).
 *
 * Patrz: [docs/ADR/0008-dev-runtime-tenant-id.md], [CLAUDE.md §6.1].
 */

const STORAGE_KEY = '__saas_dev_tenant_id';
const QUERY_PARAM = '__devTenantId';

function isDev(): boolean {
  return Boolean(import.meta.env.DEV);
}

/**
 * Zwraca tenant UUID do użycia w `X-Tenant-Id` w DEV; null gdy PROD lub brak źródła.
 *
 * Side effect: jeśli znalazł query param, zapisuje do sessionStorage i czyści URL.
 */
export function getDevTenantId(): string | null {
  if (!isDev()) return null;
  if (typeof window === 'undefined') return null;

  // 1. Query param (runtime override — Register redirect / Playwright).
  try {
    const url = new URL(window.location.href);
    const fromQuery = url.searchParams.get(QUERY_PARAM);
    if (fromQuery) {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, fromQuery);
      } catch {
        /* sessionStorage disabled (incognito quota / privacy mode) — silently ignore */
      }
      url.searchParams.delete(QUERY_PARAM);
      // replaceState: nie zaśmieca historii (back-button pozostaje intuicyjny).
      window.history.replaceState(window.history.state, '', url.toString());
      return fromQuery;
    }
  } catch {
    /* ignore — window.location.href może być exotic w niektórych runtime */
  }

  // 2. sessionStorage (persist przez tab lifecycle).
  try {
    const fromStorage = window.sessionStorage.getItem(STORAGE_KEY);
    if (fromStorage) return fromStorage;
  } catch {
    /* ignore */
  }

  // 3. Build-time env fallback (T7b.8 workflow).
  const envValue = import.meta.env.VITE_DEV_TENANT_ID;
  return typeof envValue === 'string' && envValue.length > 0 ? envValue : null;
}

/**
 * Usuwa persist-owany tenant-id z sessionStorage. Wołany przy logout żeby re-login od zera
 * zaczynał od czystego fallback (env var lub query param).
 */
export function clearDevTenantId(): void {
  if (!isDev()) return;
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * F8 T8.5 — pickup klucza odbioru impersonacji z URL i wymiana na token.
 *
 * Panel klienta otwierany przez panel administratora dostaje `#__impSessionKey=<key>`
 * w URL fragment. Funkcja:
 *  1. Wyciąga klucz z fragmentu, czyści fragment z URL (replaceState — bez nawigacji).
 *  2. Wywołuje `GET /api/auth/impersonate/redeem?key=...`.
 *  3. Stashuje zwrócony `tenantId` do sessionStorage (re-use wzorca DEV tenant-id).
 *  4. Zwraca `{tenantId, accessToken}` żeby caller (main.tsx) ustawił access token.
 *
 * Działa zarówno w DEV (same-origin same-tab) jak i PROD (cross-origin same-tab).
 * Patrz ADR-0013 (cross-origin handoff via backend session store).
 *
 * Dlaczego fragment a nie query: fragmenty NIE sa wysylane jako Referer ani do server
 * access-logow, query (`?key=...`) by wyciekal do logow API serwera dla wszystkich
 * sub-resource fetchy (bundle JS/CSS) zanim pickup zdazyl wyczyscic.
 *
 * Wywoływana w `main.tsx` przed render. Zwraca `null` gdy w URL nie ma klucza (nic do
 * zrobienia — nie błąd); `{ ok: true, ... }` po udanej wymianie; `{ ok: false, reason }`
 * gdy klucz BYŁ, ale redeem padł (404 / błąd sieci) — caller pokazuje wtedy błąd zamiast
 * cichego renderu bez tokenu.
 */
const IMPERSONATE_KEY_PARAM = '__impSessionKey';

function readImpersonationKeyFromFragment(): { key: string; clearedHash: string } | null {
  const rawHash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  if (!rawHash) return null;
  const params = new URLSearchParams(rawHash);
  const key = params.get(IMPERSONATE_KEY_PARAM);
  if (!key) return null;
  params.delete(IMPERSONATE_KEY_PARAM);
  const remaining = params.toString();
  return { key, clearedHash: remaining ? `#${remaining}` : '' };
}

export type ImpersonationPickup =
  | { ok: true; tenantId: string; accessToken: string }
  | { ok: false; reason: string };

export async function pickupImpersonationKey(): Promise<ImpersonationPickup | null> {
  if (typeof window === 'undefined') return null;

  const fragment = readImpersonationKeyFromFragment();
  if (!fragment) return null; // brak klucza w URL — nic do zrobienia (nie błąd)
  const key = fragment.key;

  // Strip z fragmentu (zawsze, niezależnie od wyniku redeem — żeby nie zostawiać śladu).
  // Zachowujemy `pathname + search`, fragment nadpisujemy `clearedHash`.
  const cleanUrl = window.location.pathname + window.location.search + fragment.clearedHash;
  window.history.replaceState(window.history.state, '', cleanUrl);

  try {
    const res = await window.fetch(`/api/auth/impersonate/redeem?key=${encodeURIComponent(key)}`);
    if (!res.ok) return { ok: false, reason: `redeem-${res.status}` };
    const data = (await res.json()) as {
      accessToken: string;
      jti: string;
      expiresAt: string;
      tenantId: string;
    };

    // Stash tenantId do dev-tenant-id sessionStorage (axios interceptor w DEV go odczyta).
    try {
      window.sessionStorage.setItem(STORAGE_KEY, data.tenantId);
    } catch {
      /* ignore — incognito quota */
    }

    return { ok: true, tenantId: data.tenantId, accessToken: data.accessToken };
  } catch {
    return { ok: false, reason: 'network' };
  }
}

/**
 * ADR-0075 — pickup klucza handoff z URL i wymiana na sesję. Bliźniak
 * `pickupImpersonationKey`, ale dla multi-tenant "wspólnego konta": po centralnym
 * logowaniu / wyborze firmy / przełączeniu frontend przekierowuje na subdomenę firmy
 * z `#__handoffKey=<key>`. Ta funkcja:
 *  1. wyciąga klucz z fragmentu, czyści fragment (replaceState),
 *  2. woła `POST /api/auth/handoff/redeem` (w DEV z nagłówkiem `X-Tenant-Id` — backend
 *     redeem wymaga tenant scope; w PROD wystarcza subdomena),
 *  3. zwraca trójstanowo (`null` = brak klucza, `{ ok: true, accessToken }` = sukces,
 *     `{ ok: false, reason }` = klucz był ale redeem padł) — main.tsx na sukcesie ustawia
 *     token przed pierwszym `/me`, na porażce pokazuje błąd zamiast cichego odbicia.
 *
 * Fragment (nie query) — jak przy impersonacji: klucz nie wycieka do logów/Referer.
 */
const HANDOFF_KEY_PARAM = '__handoffKey';

function readHandoffKeyFromFragment(): { key: string; clearedHash: string } | null {
  const rawHash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  if (!rawHash) return null;
  const params = new URLSearchParams(rawHash);
  const key = params.get(HANDOFF_KEY_PARAM);
  if (!key) return null;
  params.delete(HANDOFF_KEY_PARAM);
  const remaining = params.toString();
  return { key, clearedHash: remaining ? `#${remaining}` : '' };
}

export type HandoffPickup = { ok: true; accessToken: string } | { ok: false; reason: string };

export async function pickupHandoffKey(): Promise<HandoffPickup | null> {
  if (typeof window === 'undefined') return null;

  const fragment = readHandoffKeyFromFragment();
  if (!fragment) return null; // brak klucza w URL — nic do zrobienia (nie błąd)
  const key = fragment.key;

  const cleanUrl = window.location.pathname + window.location.search + fragment.clearedHash;
  window.history.replaceState(window.history.state, '', cleanUrl);

  // DEV: backend redeem wymaga X-Tenant-Id (host = localhost). getDevTenantId() odczytuje
  // `?__devTenantId=` z query (redirect handoff go dołącza), stashuje do sessionStorage
  // (axios interceptor użyje go do kolejnych /api calls). PROD: subdomena → header zbędny.
  const devTenantId = getDevTenantId();
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (devTenantId) headers['X-Tenant-Id'] = devTenantId;

  try {
    const res = await window.fetch('/api/auth/handoff/redeem', {
      method: 'POST',
      headers,
      body: JSON.stringify({ key }),
    });
    if (!res.ok) return { ok: false, reason: `redeem-${res.status}` };
    const data = (await res.json()) as { accessToken: string; expiresIn: number };
    return { ok: true, accessToken: data.accessToken };
  } catch {
    return { ok: false, reason: 'network' };
  }
}
